/**
 * A number that follows a polled server value but moves smoothly, one step per tick, so a counter feels alive.
 *
 * The first server value is shown LAG lower, though never lower than what is already shown, and then climbs by LAG / TICKS per tick, which brings it back to the
 * server value after one poll. Every later server value is reached by covering a TICKS-th of the gap per tick, so the
 * number lands on it right as the next poll comes in. It holds still when the server value is not ahead of it.
 * Nothing here keeps time, the owner calls tick() once per interval (poll interval / TICKS).
 */
export class LiveRamp {

  /** How much lower the first server value is shown. */
  static LAG = 45;
  /** How many ticks there are between two polls. */
  static TICKS = 15;

  private _value = 0;
  private start = 0;
  private step = 0;
  private ticks = 0;
  private primed = false;

  get value(): number {
    return this._value;
  }

  /**
   * Gives the ramp a new server value, call it for every poll.
   */
  update(serverValue: number) {

    if (!this.primed) {
      this.primed = true;
      // Never lower than what is already shown, the page may have loaded a value close to the server's
      this._value = Math.max(this._value, serverValue - LiveRamp.LAG);
      this.begin(LiveRamp.LAG / LiveRamp.TICKS);
      return;
    }

    const gap = serverValue - this._value;
    if (gap > 0) {
      this.begin(gap / LiveRamp.TICKS);
    } else {
      this.begin(0);
    }
  }

  /**
   * Moves one step towards the server value, holds once the ticks of this poll are used up.
   */
  tick() {

    if (this.ticks < LiveRamp.TICKS) {
      this.ticks++;
    }
    this._value = Math.round(this.start + this.step * this.ticks);
  }

  /**
   * Forgets everything, the next server value is treated as the first one again. The number starts at the given
   * value, which is what is already on screen so it never drops.
   */
  reset(shown: number) {

    this._value = shown;
    this.primed = false;
    this.begin(0);
  }

  private begin(step: number) {

    this.start = this._value;
    this.step = step;
    this.ticks = 0;
  }
}
